import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, Select } from '../../../ui';

const INPUT_CLASSES = 'form-input-inset min-h-11 py-2.5 leading-normal';
const TEXTAREA_CLASSES = `${INPUT_CLASSES} min-h-[120px]`;

// Base form fields for initial state (without translations)
const FORM_FIELDS = [
  { name: 'firstName', labelKey: 'userProfile.profile.fields.firstName', type: 'text', span: 'sm:col-span-3', autoComplete: 'given-name' },
  { name: 'lastName', labelKey: 'userProfile.profile.fields.lastName', type: 'text', span: 'sm:col-span-3', autoComplete: 'family-name' },
  { name: 'language', labelKey: 'profile.language', type: 'select', span: 'sm:col-span-3', options: [
    { value: 'en-US', labelKey: 'userProfile.profile.languages.en' },
    { value: 'pl-PL', labelKey: 'userProfile.profile.languages.pl' }
  ]},
  { name: 'position', labelKey: 'userProfile.profile.fields.position', type: 'select', span: 'sm:col-span-3', options: [
    { value: 'marketing_manager', labelKey: 'userProfile.profile.positions.marketing_manager' },
    { value: 'content_creator', labelKey: 'userProfile.profile.positions.content_creator' },
    { value: 'social_media_manager', labelKey: 'userProfile.profile.positions.social_media_manager' },
    { value: 'copywriter', labelKey: 'userProfile.profile.positions.copywriter' },
    { value: 'business_owner', labelKey: 'userProfile.profile.positions.business_owner' },
    { value: 'product_manager', labelKey: 'userProfile.profile.positions.product_manager' },
    { value: 'hr_specialist', labelKey: 'userProfile.profile.positions.hr_specialist' },
    { value: 'other', labelKey: 'userProfile.profile.other' }
  ]},
  { name: 'email', labelKey: 'userProfile.profile.fields.email', type: 'email', span: 'sm:col-span-3', autoComplete: 'email', readOnly: true },
  { name: 'title', labelKey: 'common.title', type: 'text', span: 'sm:col-span-3' },
  { name: 'description', labelKey: 'common.description', type: 'textarea', span: 'col-span-full' },
  { name: 'goals', labelKey: 'userProfile.profile.fields.goals', type: 'multiselect', span: 'col-span-full', options: [
    { value: 'blog_posts', labelKey: 'userProfile.profile.goals.blog_posts' },
    { value: 'ecommerce', labelKey: 'userProfile.profile.goals.ecommerce' },
    { value: 'social_media', labelKey: 'userProfile.profile.goals.social_media' },
    { value: 'ai_assistant', labelKey: 'userProfile.profile.goals.ai_assistant' },
    { value: 'custom_content', labelKey: 'userProfile.profile.goals.custom_content' },
    { value: 'other', labelKey: 'userProfile.profile.other' }
  ]},
];

const getFormFields = (t) => FORM_FIELDS.map(field => ({
  ...field,
  label: t(field.labelKey),
  options: field.options?.map((option) => ({
    ...option,
    label: t(option.labelKey)
  }))
}));

const ProfileTab = ({
  formData,
  handleChange,
  handleImageUpload,
  setSnackbarMessage,
  setSnackbarType,
  setShowSnackbar
}) => {
  const { t } = useTranslation();
  const [avatarDataUrl, setAvatarDataUrl] = useState(null);
  const translatedFormFields = getFormFields(t);

  useEffect(() => {
    const convertAvatarToDataUrl = async () => {
      if (formData.avatar) {
        try {
          const response = await fetch(formData.avatar, {
            mode: 'cors',
            credentials: 'same-origin'
          });
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setAvatarDataUrl(reader.result);
          };
          reader.readAsDataURL(blob);
        } catch (error) {
          console.error('Error converting avatar to data URL:', error);
          setAvatarDataUrl(null);
        }
      } else {
        setAvatarDataUrl(null);
      }
    };

    convertAvatarToDataUrl();
  }, [formData.avatar]);

  const renderField = ({ name, label, type, span, autoComplete, options, readOnly }) => (
    <div key={name} className={span}>
      {type === 'textarea' ? (
        <>
          <label htmlFor={name} className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          <div className="mt-2">
            <textarea
              name={name}
              id={name}
              value={formData[name]}
              onChange={handleChange}
              rows={3}
              className={TEXTAREA_CLASSES}
            />
          </div>
        </>
      ) : type === 'select' ? (
        <FormField label={label} htmlFor={name}>
          <Select
            name={name}
            id={name}
            value={formData[name]}
            onChange={handleChange}
            className="min-h-11 py-2.5 leading-normal"
          >
            <option value="">{t('userProfile.profile.selectField', { field: label })}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormField>
      ) : type === 'multiselect' ? (
        <>
          <label htmlFor={name} className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          <div className="mt-2 grid grid-cols-2 gap-4">
            {options.map((option) => (
              <label key={option.value} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name={name}
                  value={option.value}
                  checked={(formData[name] || '').includes(option.value)}
                  onChange={(e) => {
                    const currentGoals = formData[name] ? formData[name].split(',').map(g => g.trim()) : [];
                    const newGoals = e.target.checked
                      ? [...currentGoals, option.value]
                      : currentGoals.filter(g => g !== option.value);
                    handleChange({
                      target: {
                        name,
                        value: newGoals.join(', ')
                      }
                    });
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </>
      ) : (
        <>
          <label htmlFor={name} className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          <div className="mt-2">
            <input
              type={type}
              name={name}
              id={name}
              value={formData[name]}
              onChange={handleChange}
              autoComplete={autoComplete}
              readOnly={readOnly}
              aria-readonly={readOnly || undefined}
              className={INPUT_CLASSES}
            />
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-12">
      <div className="pb-12">
        <h2 className="text-3xl font-medium leading-7 text-gray-900">{t('userProfile.tabs.profile')}</h2>
        <p className="mt-4 text-sm leading-6 text-gray-600">
          {t('userProfile.profile.description')}
        </p>

        <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          <div className="col-span-full">
            <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
              {t('userProfile.profile.photo')}
            </label>
            <div className="mt-2 flex items-center gap-x-3">
              {avatarDataUrl ? (
                <img
                  src={avatarDataUrl}
                  alt={t('userProfile.tabs.profile')}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center text-base font-regular text-primary-500">
                  {(formData.firstName?.substring(0, 1) + formData.lastName?.substring(0, 1)).toUpperCase()}
                </div>
              )}
              <input
                type="file"
                id="photo"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImageUpload(file)
                      .then(() => {
                        setSnackbarMessage(t('userProfile.profile.photoUpdated'));
                        setSnackbarType('success');
                        setShowSnackbar(true);
                      })
                      .catch((error) => {
                        setSnackbarMessage(t('userProfile.profile.photoUploadFailed', { error: error.message }));
                        setSnackbarType('error');
                        setShowSnackbar(true);
                      });
                  }
                }}
              />
              <button
                type="button"
                onClick={() => document.getElementById('photo').click()}
                className="inline-flex items-center rounded-md bg-white px-4 h-11 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                {t('common.edit')}
              </button>
            </div>
          </div>

          {translatedFormFields.map(renderField)}
        </div>
      </div>
    </div>
  );
};

export { ProfileTab, FORM_FIELDS };
